import React, { useEffect, useMemo, useState } from "react";
import BotonManualHeader from "../../components/BotonManualHeader"; // Re-saved to trigger reload
import jsPDF from "jspdf";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  OverlayTrigger,
  Row,
  Spinner,
  Stack,
  Tab,
  Table,
  Tabs,
  Tooltip,
} from "react-bootstrap";
import { FaCalendarAlt, FaDownload, FaEdit, FaFileAlt, FaFileSignature, FaPaperPlane, FaPlus, FaSearch, FaSyncAlt, FaTrash } from "react-icons/fa";
import { collection, deleteDoc, doc, getDoc, getDocs, getFirestore, query, serverTimestamp, setDoc, where, writeBatch } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import { logBusinessAction } from "../../services/AuditService";
import sendCustomEmail from "../../functions/sendCustomEmail";
import { sendNotificationBatch } from "../../functions/sendNotification";
import {
  buildContratoPdfHtml,
  buildContratoSections,
  calcularFinPeriodoPrueba,
  formatDateEsPe,
  formatMonto,
  formatSoles,
  hasRenderableText,
  htmlToTextPreserveBoldMarkers,
  normalizeDisplayLine,
  PDF_MARGIN_PRESETS,
  splitParagraphsRaw,
  stripBoldMarkers,
} from "./contratos/contractBuilderUtils";

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

const toSafeText = (value) => String(value ?? "").trim();
const getUserRuc = (usuario = {}) =>
  toSafeText(usuario?.ruc || usuario?.ruc_empresa || usuario?.RUC || usuario?.rucEmpresa);

const sortByRecentTimestamp = (a, b) => {
  const toMillis = (item) => {
    const ts = item?.updatedAt || item?.createdAt;
    if (!ts) return 0;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (ts?.seconds) return ts.seconds * 1000;
    if (typeof ts === "number") return ts;
    return 0;
  };
  return toMillis(b) - toMillis(a);
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "-";
  if (typeof timestamp?.toDate === "function") return timestamp.toDate().toLocaleString("es-PE");
  if (timestamp?.seconds) return new Date(timestamp.seconds * 1000).toLocaleString("es-PE");
  return "-";
};

const buildWorkerContractData = ({ worker, template, usuario, rucEmpresa, assignmentData, companyData }) => {
  const templateDefaults = template?.templateDefaults && typeof template.templateDefaults === "object"
    ? template.templateDefaults
    : {};
  const fullName = `${toSafeText(worker?.nombres)} ${toSafeText(worker?.apellidos)}`.trim();
  const workerDni = toSafeText(worker?.dni || worker?.id);
  const empresa = toSafeText(
    templateDefaults?.empresa ||
    template?.empresa ||
    template?.razon_social ||
    companyData?.name ||
    companyData?.nombreComercial ||
    usuario?.empresa ||
    usuario?.empresaNombreComercial
  );
  const representative = toSafeText(
    assignmentData?.representante_legal ||
    templateDefaults?.representante_legal ||
    templateDefaults?.firma_empleador_nombre ||
    template?.representante_legal ||
    companyData?.representante_legal ||
    companyData?.representanteLegal ||
    companyData?.representante ||
    usuario?.representante_legal ||
    usuario?.firma_empleador_nombre ||
    ""
  );
  const representativeCargo = toSafeText(
    assignmentData?.representante_legal_cargo ||
    templateDefaults?.representante_legal_cargo ||
    templateDefaults?.firma_empleador_cargo ||
    template?.representante_legal_cargo ||
    companyData?.representante_legal_cargo ||
    companyData?.representanteCargo ||
    usuario?.representante_legal_cargo ||
    usuario?.firma_empleador_cargo ||
    "Gerente General"
  );
  const empleadorDomicilio = toSafeText(
    templateDefaults?.empleador_domicilio_legal ||
    companyData?.direccion ||
    usuario?.direccion ||
    ""
  );
  const puesto = toSafeText(assignmentData?.puesto || worker?.cargo);
  const rawSalary = toSafeText(assignmentData?.salario || worker?.sueldo_base || worker?.sueldo_basico);
  const salarioSoles = rawSalary ? formatSoles(rawSalary) : "";
  const salarioMonto = rawSalary ? formatMonto(rawSalary) : "";
  const fechaInicio = toSafeText(assignmentData?.fechaInicio);
  const fechaFin = toSafeText(assignmentData?.fechaFin);
  const tipoContrato = toSafeText(template?.contractType) || "GENERAL";
  const domicilio = toSafeText(worker?.domicilio || worker?.direccion);
  const telefono = toSafeText(assignmentData?.telefono || worker?.telefono || worker?.phone || worker?.celular);
  const correo = toSafeText(assignmentData?.correo || worker?.email || worker?.correo);
  const periodoPruebaMeses = toSafeText(assignmentData?.periodo_prueba_meses || templateDefaults?.periodo_prueba_meses) || "3";
  const finPruebaCalculada = fechaInicio ? calcularFinPeriodoPrueba(fechaInicio, periodoPruebaMeses) : "";
  const fechaFinPeriodoPrueba = toSafeText(
    assignmentData?.fecha_fin_periodo_prueba || (finPruebaCalculada ? formatDateEsPe(finPruebaCalculada) : "")
  );
  const lugarTrabajo = toSafeText(templateDefaults?.lugar_trabajo || companyData?.direccion || domicilio);

  return {
    empresa,
    ruc_empresa: rucEmpresa,
    empleador_domicilio_legal: empleadorDomicilio,
    representante_legal: representative,
    representante_legal_cargo: representativeCargo,
    trabajador_nombre: fullName,
    trabajador_dni: workerDni,
    trabajador_domicilio: domicilio,
    trabajador_telefono: telefono,
    trabajador_correo: correo,
    telefono,
    correo,
    lugar_trabajo: lugarTrabajo,
    cargo: puesto,
    sueldo: salarioSoles,
    monto_sueldo: salarioMonto,
    moneda_sueldo: "Soles",
    fecha_inicio: fechaInicio,
    fecha_fin: fechaFin,
    periodo_prueba_meses: periodoPruebaMeses,
    fecha_fin_periodo_prueba: fechaFinPeriodoPrueba,
    tipo_contrato: toSafeText(templateDefaults?.tipo_contrato) || tipoContrato,
    jornada: toSafeText(templateDefaults?.jornada),
    ciudad_firma: toSafeText(templateDefaults?.ciudad_firma || companyData?.distrito || companyData?.provincia || "Lima"),
    fecha_firma: toSafeText(templateDefaults?.fecha_firma),
    firma_empleador_nombre: representative,
    firma_empleador_cargo: representativeCargo,
    firma_trabajador_nombre: fullName,
    firma_trabajador_dni: workerDni,
  };
};

const buildContractHtmlFromTemplate = ({ template, dataMap }) => {
  const clauses = Array.isArray(template?.clauses) ? template.clauses : [];
  const templateName = toSafeText(template?.templateName || "CONTRATO DE TRABAJO");
  const contractTitle = toSafeText(template?.contractTitle || templateName);
  const sections = buildContratoSections(clauses, dataMap, templateName, {
    boldVariables: true,
    contractTitle,
  });
  return buildContratoPdfHtml(sections, { innerPaddingPx: 0 });
};

const toDataUrlFromImageUrl = async (url) => {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`No se pudo cargar firma (${response.status})`);
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

const getImageSizeFromDataUrl = async (dataUrl) =>
  await new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
    img.onerror = reject;
    img.src = dataUrl;
  });

const exportPdfLikeGenerador = async (
  sections,
  fileName,
  pdfMarginPreset = "NORMAL",
  firmaGGUrl = "",
  options = { download: true }
) => {
  const selectedPdfMargin = PDF_MARGIN_PRESETS[pdfMarginPreset] || PDF_MARGIN_PRESETS.NORMAL;
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = selectedPdfMargin.pageMarginPt;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const textWidth = pageWidth - margin * 2;
  let y = margin;
  const lineHeight = 13;
  const paragraphGap = 6;
  const sectionGap = 12;

  const ensureSpace = (required = lineHeight) => {
    if (y + required > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const tokenizeStyledText = (text) => {
    const normalized = String(text || "").replace(/\\\*/g, "*").replace(/\*{3,}/g, "**");
    const parts = normalized.split(/(\*\*[\s\S]+?\*\*)/g);
    const tokens = [];
    parts.forEach((part) => {
      if (!part) return;
      const isBold = /^\*\*[\s\S]+?\*\*$/.test(part);
      const raw = (isBold ? part.slice(2, -2) : part).replace(/\*\*/g, "");
      raw.split(/(\s+)/).forEach((chunk) => {
        if (chunk) tokens.push({ text: chunk, bold: isBold });
      });
    });
    return tokens;
  };

  const tokenWidth = (text, bold) => {
    doc.setFont("times", bold ? "bold" : "normal");
    doc.setFontSize(10);
    return doc.getTextWidth(text);
  };

  const wrapStyledTokens = (tokens, maxWidth) => {
    const lines = [];
    let current = [];
    let currentWidth = 0;

    const flush = () => {
      while (current.length && /^\s+$/.test(current[current.length - 1].text)) current.pop();
      if (current.length) lines.push(current);
      current = [];
      currentWidth = 0;
    };

    tokens.forEach((token) => {
      let text = token.text;
      if (!current.length) text = text.replace(/^\s+/, "");
      if (!text) return;

      const width = tokenWidth(text, token.bold);
      if (current.length && currentWidth + width > maxWidth && !/^\s+$/.test(text)) {
        flush();
        text = text.replace(/^\s+/, "");
        if (!text) return;
      }

      current.push({ text, bold: token.bold });
      currentWidth += tokenWidth(text, token.bold);
    });

    flush();
    return lines;
  };

  const lineNaturalWidth = (lineTokens) => lineTokens.reduce((total, token) => total + tokenWidth(token.text, token.bold), 0);
  const countWhitespaceTokens = (lineTokens) => lineTokens.reduce((count, token) => (/\s+/.test(token.text) ? count + 1 : count), 0);

  const drawStyledLine = (lineTokens, { justify = false } = {}) => {
    let x = margin;
    const naturalWidth = lineNaturalWidth(lineTokens);
    const whitespaceCount = countWhitespaceTokens(lineTokens);
    let extraPerGap = 0;
    if (justify && whitespaceCount > 0) {
      const extra = Math.max(0, textWidth - naturalWidth);
      if (extra > 0 && naturalWidth > textWidth * 0.62) extraPerGap = extra / whitespaceCount;
    }
    lineTokens.forEach((token) => {
      const width = tokenWidth(token.text, token.bold);
      if (/\s+/.test(token.text)) {
        x += width + extraPerGap;
        return;
      }
      doc.setFont("times", token.bold ? "bold" : "normal");
      doc.setFontSize(10);
      doc.text(token.text, x, y);
      x += width;
    });
  };

  const writeParagraph = (rawText) => {
    const paragraph = String(rawText || "")
      .replace(/\\\*/g, "*")
      .replace(/\*{3,}/g, "**")
      .replace(/\r\n/g, "\n")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
    if (!paragraph) return;

    const hasInlineBold = /\*\*[\s\S]+?\*\*/.test(paragraph);
    if (hasInlineBold) {
      const tokens = tokenizeStyledText(paragraph);
      const lines = wrapStyledTokens(tokens, textWidth);
      ensureSpace(lines.length * lineHeight + paragraphGap);
      lines.forEach((lineTokens, lineIndex) => {
        drawStyledLine(lineTokens, { justify: lineIndex < lines.length - 1 });
        y += lineHeight;
      });
      y += paragraphGap;
      return;
    }

    const text = stripBoldMarkers(paragraph).replace(/\*\*/g, "");
    const lines = doc.splitTextToSize(text, textWidth);
    ensureSpace(lines.length * lineHeight + paragraphGap);
    doc.setFont("times", "normal");
    doc.setFontSize(10);
    doc.text(lines, margin, y, { align: "justify", maxWidth: textWidth });
    y += lines.length * lineHeight + paragraphGap;
  };

  const writeCenteredTitle = (title) => {
    const rawTitleLines = String(title || "").split("\n").map((line) => normalizeDisplayLine(line)).filter(Boolean);
    if (!rawTitleLines.length) return;
    const lines = rawTitleLines.flatMap((line) => doc.splitTextToSize(line, textWidth));
    doc.setFont("times", "bold");
    doc.setFontSize(11);
    ensureSpace(lines.length * lineHeight + sectionGap);
    doc.text(lines, pageWidth / 2, y, { align: "center", maxWidth: textWidth });
    y += lines.length * lineHeight + sectionGap;
  };

  writeCenteredTitle(sections.title);
  const introForPdf = hasRenderableText(sections.introHtml)
    ? splitParagraphsRaw(htmlToTextPreserveBoldMarkers(sections.introHtml))
    : sections.introParagraphs;
  introForPdf.forEach((p) => writeParagraph(p));
  if (introForPdf.length) y += 4;

  sections.clauses.forEach((clause) => {
    const clauseParagraphsForPdf = hasRenderableText(clause.html)
      ? splitParagraphsRaw(htmlToTextPreserveBoldMarkers(clause.html))
      : clause.paragraphs;
    clauseParagraphsForPdf.forEach((p) => writeParagraph(p));
    y += sectionGap - paragraphGap;
  });

  const { employerName, employerRole, employeeName, employeeDni } = sections.signatures;
  const safeFirmaUrl = toSafeText(firmaGGUrl);
  if (employerName || employeeName || safeFirmaUrl) {
    ensureSpace(180);
    y += 62;
    const colWidth = (textWidth - 40) / 2;
    const leftX = margin;
    const rightX = margin + colWidth + 40;

    if (safeFirmaUrl) {
      try {
        const dataUrl = safeFirmaUrl.startsWith("data:image/")
          ? safeFirmaUrl
          : await toDataUrlFromImageUrl(safeFirmaUrl);
        const imageFormat = dataUrl.startsWith("data:image/png") ? "PNG" : "JPEG";
        const natural = await getImageSizeFromDataUrl(dataUrl);
        const imgW = 200;
        const ratio = natural?.width && natural?.height ? natural.height / natural.width : 0.3;
        const imgH = Math.max(40, Math.round(imgW * ratio));
        const imgX = leftX + colWidth / 2 - imgW / 2;
        const imgY = y - Math.round(imgH * 0.6);
        doc.addImage(dataUrl, imageFormat, imgX, imgY, imgW, imgH, undefined, "FAST");
      } catch (e) {
        console.warn("No se pudo incrustar firmaGG en PDF:", e);
      }
    }

    doc.setDrawColor(80);
    const lineY = y + 34;
    doc.line(leftX, lineY, leftX + colWidth, lineY);
    doc.line(rightX, lineY, rightX + colWidth, lineY);
    doc.setFont("times", "bold");
    doc.setFontSize(9);
    doc.text("EL EMPLEADOR", leftX + colWidth / 2, lineY + 14, { align: "center" });
    doc.text("EL TRABAJADOR", rightX + colWidth / 2, lineY + 14, { align: "center" });
    doc.setFont("times", "normal");
    if (employerName) doc.text(stripBoldMarkers(employerName), leftX + colWidth / 2, lineY + 28, { align: "center" });
    if (employeeName) doc.text(stripBoldMarkers(employeeName), rightX + colWidth / 2, lineY + 28, { align: "center" });
    if (employerRole) doc.text(stripBoldMarkers(employerRole), leftX + colWidth / 2, lineY + 42, { align: "center" });
    if (employeeDni) doc.text(`DNI: ${stripBoldMarkers(employeeDni)}`, rightX + colWidth / 2, lineY + 42, { align: "center" });
  }

  if (options?.download !== false) {
    doc.save(fileName);
    return null;
  }
  return doc.output("blob");
};

const sanitizeTitleForFileName = (value) =>
  String(value || "Contrato de trabajo")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const buildUniqueStorageSuffix = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const random = Math.floor(1000 + Math.random() * 9000);
  return `${timestamp}-${random}`;
};

const fetchCompanyFirmaGG = async (ruc) => {
  const safeRuc = toSafeText(ruc);
  if (!safeRuc) return "";
  try {
    const companySnap = await getDoc(doc(db, "company", safeRuc));
    if (!companySnap.exists()) return "";
    return toSafeText(companySnap.data()?.firmaGG);
  } catch (err) {
    console.warn("No se pudo obtener firmaGG desde company:", err);
    return "";
  }
};

const resolveFirmaGGDataUrl = async ({ assignmentRuc, companyFirmaGG, assignmentFirmaGG }) => {
  const latestCompanyFirma = await fetchCompanyFirmaGG(assignmentRuc);
  const candidates = [
    toSafeText(latestCompanyFirma),
    toSafeText(companyFirmaGG),
    toSafeText(assignmentFirmaGG),
  ].filter(Boolean);

  for (const candidate of candidates) {
    try {
      if (candidate.startsWith("data:image/")) return candidate;
      // eslint-disable-next-line no-await-in-loop
      const dataUrl = await toDataUrlFromImageUrl(candidate);
      if (dataUrl) return dataUrl;
    } catch (err) {
      console.warn("FirmaGG no utilizable, intentando siguiente fuente:", err);
    }
  }
  return "";
};

const isAssignmentAlreadySent = (assignment = {}) => {
  const estado = toSafeText(assignment?.estado).toUpperCase();
  return estado === "ENVIADO" || Boolean(assignment?.onboardingDocumentId || assignment?.onboardingLink);
};

const isAssignmentSigned = (assignment = {}) => {
  const firmaEstado = toSafeText(assignment?.firmaEstado).toUpperCase();
  return (
    firmaEstado === "FIRMADO" ||
    assignment?.firmaCompletado === true ||
    Boolean(assignment?.linkFirmado || assignment?.firmaFecha)
  );
};

function ContratosPlantillas({ usuario }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [companyData, setCompanyData] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [sendingMassive, setSendingMassive] = useState(false);
  const [updatingDates, setUpdatingDates] = useState(false);
  const [updatingDatesMassive, setUpdatingDatesMassive] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [assignmentMessage, setAssignmentMessage] = useState("");
  const [sendResults, setSendResults] = useState([]);
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState("TODOS");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [assignmentData, setAssignmentData] = useState({
    fechaInicio: "",
    fechaFin: "",
    salario: "",
    puesto: "",
    representante_legal: "",
    representante_legal_cargo: "Gerente General",
    telefono: "",
    correo: "",
    fecha_fin_periodo_prueba: "",
    periodo_prueba_meses: "3",
  });
  const rucEmpresa = getUserRuc(usuario);

  const loadTemplates = async () => {
    if (!rucEmpresa) {
      setTemplates([]);
      setError("No se pudo identificar el RUC de la empresa.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const byRucQuery = query(collection(db, "contratoPlantillas"), where("ruc", "==", rucEmpresa));
      const byRucEmpresaQuery = query(collection(db, "contratoPlantillas"), where("ruc_empresa", "==", rucEmpresa));
      const [byRucSnap, byRucEmpresaSnap] = await Promise.all([
        getDocs(byRucQuery),
        getDocs(byRucEmpresaQuery),
      ]);

      const merged = new Map();
      [...byRucSnap.docs, ...byRucEmpresaSnap.docs].forEach((docRef) => {
        merged.set(docRef.id, { id: docRef.id, ...docRef.data() });
      });

      setTemplates(Array.from(merged.values()).sort(sortByRecentTimestamp));
    } catch (err) {
      console.error("Error cargando plantillas de contrato:", err);
      setError("No se pudieron cargar las plantillas de contrato.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rucEmpresa]);

  useEffect(() => {
    const loadCompany = async () => {
      if (!rucEmpresa) {
        setCompanyData(null);
        return;
      }
      try {
        const snap = await getDoc(doc(db, "company", rucEmpresa));
        setCompanyData(snap.exists() ? snap.data() : null);
      } catch (err) {
        console.warn("No se pudo cargar datos de company para firma:", err);
        setCompanyData(null);
      }
    };
    loadCompany();
  }, [rucEmpresa]);

  const loadAssignments = async () => {
    if (!rucEmpresa) {
      setAssignments([]);
      return;
    }
    try {
      setLoadingAssignments(true);
      const q1 = query(collection(db, "contratoAsignaciones"), where("ruc", "==", rucEmpresa));
      const q2 = query(collection(db, "contratoAsignaciones"), where("ruc_empresa", "==", rucEmpresa));
      const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);
      const merged = new Map();
      [...s1.docs, ...s2.docs].forEach((d) => merged.set(d.id, { id: d.id, ...d.data() }));

      const rows = Array.from(merged.values());
      const withFirmaStatus = await Promise.all(
        rows.map(async (item) => {
          const trabajadorId = toSafeText(item?.trabajadorId);
          const onboardingDocumentId = toSafeText(item?.onboardingDocumentId);
          const onboardingLegajoId = toSafeText(item?.onboardingLegajoId);

          if (!trabajadorId || !onboardingDocumentId) {
            return {
              ...item,
              firmaEstado: isAssignmentAlreadySent(item) ? "PENDIENTE" : "NO_ENVIADO",
              firmaCompletado: false,
              firmaFecha: null,
              linkFirmado: "",
            };
          }

          try {
            const [firmaDocSnap, legajoDocSnap] = await Promise.all([
              getDoc(doc(db, `onboarding/${trabajadorId}/documentos`, onboardingDocumentId)),
              onboardingLegajoId
                ? getDoc(doc(db, `onboarding/${trabajadorId}/legajo`, onboardingLegajoId))
                : Promise.resolve(null),
            ]);

            const legajoData = legajoDocSnap?.exists?.() ? (legajoDocSnap.data() || {}) : {};
            const linkFirmado = toSafeText(legajoData.linkFirmado || "");
            const fechaFirmaLegajo = legajoData.fechaFirma || null;

            if (!firmaDocSnap.exists()) {
              const firmadoPorLegajo = Boolean(linkFirmado || fechaFirmaLegajo);
              return {
                ...item,
                firmaEstado: firmadoPorLegajo ? "FIRMADO" : "PENDIENTE",
                firmaCompletado: firmadoPorLegajo,
                firmaFecha: fechaFirmaLegajo,
                linkFirmado,
              };
            }

            const firmaData = firmaDocSnap.data() || {};
            const fechaFirmaDoc = firmaData.completedAt || firmaData.fechaFirma || firmaData.firmadoAt || null;
            const completed = firmaData.completed === true || Boolean(linkFirmado || fechaFirmaLegajo || fechaFirmaDoc);
            return {
              ...item,
              firmaEstado: completed ? "FIRMADO" : "PENDIENTE",
              firmaCompletado: completed,
              firmaFecha:
                fechaFirmaLegajo ||
                fechaFirmaDoc ||
                null,
              linkFirmado,
            };
          } catch (firmaErr) {
            console.warn("No se pudo leer estado de firma del contrato:", firmaErr);
            return {
              ...item,
              firmaEstado: "SIN_DATO",
              firmaCompletado: false,
              firmaFecha: null,
              linkFirmado: "",
            };
          }
        })
      );

      setAssignments(withFirmaStatus.sort(sortByRecentTimestamp));
    } catch (err) {
      console.error("Error cargando asignaciones:", err);
      setError("No se pudo cargar la tabla de contratos asignados.");
    } finally {
      setLoadingAssignments(false);
    }
  };

  useEffect(() => {
    loadAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rucEmpresa]);

  const workersById = useMemo(() => {
    const map = new Map();
    workers.forEach((worker) => map.set(worker.id, worker));
    return map;
  }, [workers]);

  const selectedTemplate = useMemo(
    () => templates.find((item) => item.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const [searchAssignment, setSearchAssignment] = useState("");
  const [filterMonth, setFilterMonth] = useState("TODOS");
  const [filterYear, setFilterYear] = useState("TODOS");

  const selectedWorker = useMemo(
    () => workersById.get(selectedWorkerId) || null,
    [workersById, selectedWorkerId]
  );

  const availableYears = useMemo(() => {
    const years = new Set();
    assignments.forEach((item) => {
      const dateStr = item?.contratoFechaInicio || item?.fecha_inicio_contrato;
      if (dateStr) {
        const parts = String(dateStr).split("-");
        if (parts[0] && parts[0].length === 4) {
          years.add(parts[0]);
        }
      }
    });
    return Array.from(years).sort().reverse();
  }, [assignments]);

  const filteredAssignmentsList = useMemo(() => {
    return assignments.filter((item) => {
      const matchesStatus =
        assignmentStatusFilter === "TODOS" ||
        (assignmentStatusFilter === "BORRADOR" && !isAssignmentAlreadySent(item)) ||
        (assignmentStatusFilter === "ENVIADO" && isAssignmentAlreadySent(item) && !isAssignmentSigned(item)) ||
        (assignmentStatusFilter === "FIRMADO" && isAssignmentSigned(item));

      const name = toSafeText(item.trabajadorNombre).toLowerCase();
      const dniVal = toSafeText(item.trabajadorId).toLowerCase();
      const term = searchAssignment.toLowerCase().trim();
      const matchesSearch = !term || name.includes(term) || dniVal.includes(term);

      let matchesMonth = true;
      const dateStr = item?.contratoFechaInicio || item?.fecha_inicio_contrato;
      if (filterMonth !== "TODOS" && dateStr) {
        const parts = String(dateStr).split("-");
        if (parts.length >= 2) {
          const monthNum = parseInt(parts[1], 10);
          matchesMonth = monthNum === parseInt(filterMonth, 10);
        } else {
          matchesMonth = false;
        }
      }

      let matchesYear = true;
      if (filterYear !== "TODOS" && dateStr) {
        const parts = String(dateStr).split("-");
        if (parts.length >= 1) {
          matchesYear = parts[0] === filterYear;
        } else {
          matchesYear = false;
        }
      }

      return matchesStatus && matchesSearch && matchesMonth && matchesYear;
    });
  }, [assignments, assignmentStatusFilter, searchAssignment, filterMonth, filterYear]);

  const pendingAssignmentsCount = useMemo(
    () => filteredAssignmentsList.filter((item) => !isAssignmentAlreadySent(item)).length,
    [filteredAssignmentsList]
  );

  const signedAssignmentsCount = useMemo(
    () => filteredAssignmentsList.filter((item) => isAssignmentSigned(item)).length,
    [filteredAssignmentsList]
  );

  const loadWorkers = async () => {
    if (!rucEmpresa) {
      setWorkers([]);
      setSelectedWorkerId("");
      return;
    }
    try {
      setLoadingWorkers(true);
      const byRucEmpresaQuery = query(collection(db, "pl_empleados"), where("ruc_empresa", "==", rucEmpresa));
      const byRucQuery = query(collection(db, "pl_empleados"), where("ruc", "==", rucEmpresa));
      const [byRucEmpresaSnap, byRucSnap] = await Promise.all([
        getDocs(byRucEmpresaQuery),
        getDocs(byRucQuery),
      ]);
      const merged = new Map();
      [...byRucEmpresaSnap.docs, ...byRucSnap.docs].forEach((docRef) => {
        merged.set(docRef.id, { id: docRef.id, ...docRef.data() });
      });
      const rows = Array.from(merged.values()).sort((a, b) => {
        const fullA = `${toSafeText(a.apellidos)} ${toSafeText(a.nombres)}`.trim();
        const fullB = `${toSafeText(b.apellidos)} ${toSafeText(b.nombres)}`.trim();
        return fullA.localeCompare(fullB, "es", { sensitivity: "base" });
      });
      setWorkers(rows);
      if (rows.length && !rows.some((w) => w.id === selectedWorkerId)) {
        setSelectedWorkerId(rows[0].id);
      }
    } catch (err) {
      console.error("Error cargando trabajadores:", err);
      setError("No se pudieron cargar los trabajadores de la empresa.");
    } finally {
      setLoadingWorkers(false);
    }
  };

  useEffect(() => {
    loadWorkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rucEmpresa]);

  useEffect(() => {
    if (!selectedWorker) return;
    const fInicio = toSafeText(selectedWorker.fecha_inicio_contrato || selectedWorker.fecha_ingreso);
    const finPruebaIso = fInicio ? calcularFinPeriodoPrueba(fInicio, 3) : "";
    setAssignmentData((prev) => ({
      ...prev,
      fechaInicio: fInicio,
      fechaFin: toSafeText(selectedWorker.fecha_fin_contrato),
      salario: toSafeText(selectedWorker.sueldo_base || selectedWorker.sueldo_basico),
      puesto: toSafeText(selectedWorker.cargo),
      telefono: toSafeText(selectedWorker.telefono || selectedWorker.phone || selectedWorker.celular),
      correo: toSafeText(selectedWorker.email || selectedWorker.correo),
      fecha_fin_periodo_prueba: finPruebaIso ? formatDateEsPe(finPruebaIso) : prev.fecha_fin_periodo_prueba,
    }));
  }, [selectedWorker]);

  useEffect(() => {
    if (!selectedTemplate && !companyData) return;
    const templateDefaults = selectedTemplate?.templateDefaults || {};
    const rep = toSafeText(
      templateDefaults.representante_legal ||
      templateDefaults.firma_empleador_nombre ||
      selectedTemplate?.representante_legal ||
      companyData?.representante_legal ||
      companyData?.representanteLegal ||
      companyData?.representante ||
      usuario?.representante_legal ||
      ""
    );
    const cargo = toSafeText(
      templateDefaults.representante_legal_cargo ||
      templateDefaults.firma_empleador_cargo ||
      selectedTemplate?.representante_legal_cargo ||
      companyData?.representante_legal_cargo ||
      companyData?.representanteCargo ||
      usuario?.representante_legal_cargo ||
      "Gerente General"
    );

    setAssignmentData((prev) => ({
      ...prev,
      representante_legal: prev.representante_legal || rep,
      representante_legal_cargo: prev.representante_legal_cargo || cargo,
    }));
  }, [selectedTemplate, companyData, usuario]);

  const filteredTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return templates;
    return templates.filter((item) =>
      [item.templateName, item.contractType, item.createdBy]
        .map((value) => String(value || "").toLowerCase())
        .join(" ")
        .includes(term)
    );
  }, [templates, search]);
  const templatesWithType = useMemo(
    () => filteredTemplates.filter((item) => toSafeText(item.contractType).length > 0).length,
    [filteredTemplates]
  );
  const templatesWithoutType = useMemo(
    () => filteredTemplates.filter((item) => toSafeText(item.contractType).length === 0).length,
    [filteredTemplates]
  );

  const goToGenerator = (templateId = "") => {
    if (!templateId) {
      navigate("/documentos/contratos");
      return;
    }
    navigate(`/documentos/contratos?plantillaId=${encodeURIComponent(templateId)}`);
  };

  const pickTemplateForAssignment = (templateId) => {
    setSelectedTemplateId(templateId);
    setAssignmentMessage("");
  };

  const handleAssignTemplate = async () => {
    setAssignmentMessage("");
    if (!selectedTemplate) {
      setAssignmentMessage("Selecciona una plantilla para asignar.");
      return;
    }
    if (!selectedWorker) {
      setAssignmentMessage("Selecciona un trabajador.");
      return;
    }
    if (!toSafeText(assignmentData.fechaInicio)) {
      setAssignmentMessage("La fecha de inicio es obligatoria.");
      return;
    }
    if (!toSafeText(assignmentData.salario)) {
      setAssignmentMessage("El salario es obligatorio.");
      return;
    }
    if (!toSafeText(assignmentData.puesto)) {
      setAssignmentMessage("El puesto es obligatorio.");
      return;
    }

    const workerDni = toSafeText(selectedWorker.dni || selectedWorker.id);
    if (!workerDni) {
      setAssignmentMessage("No se encontró DNI para el trabajador seleccionado.");
      return;
    }

    const payload = {
      contratoPlantillaId: selectedTemplate.id,
      contratoPlantillaNombre: toSafeText(selectedTemplate.templateName),
      contratoTipo: toSafeText(selectedTemplate.contractType) || "GENERAL",
      contratoFechaInicio: toSafeText(assignmentData.fechaInicio),
      contratoFechaFin: toSafeText(assignmentData.fechaFin),
      fecha_inicio_contrato: toSafeText(assignmentData.fechaInicio),
      fecha_fin_contrato: toSafeText(assignmentData.fechaFin),
      fecha_fin_periodo_prueba: toSafeText(assignmentData.fecha_fin_periodo_prueba),
      sueldo_base: Number(assignmentData.salario) || 0,
      cargo: toSafeText(assignmentData.puesto),
      representante_legal: toSafeText(assignmentData.representante_legal),
      representante_legal_cargo: toSafeText(assignmentData.representante_legal_cargo),
      telefono: toSafeText(assignmentData.telefono),
      correo: toSafeText(assignmentData.correo),
      contratoAsignadoAt: serverTimestamp(),
      contratoAsignadoPor: toSafeText(usuario?.email),
    };

    try {
      setSavingAssignment(true);
      const firmaGGActual =
        toSafeText(companyData?.firmaGG) ||
        await fetchCompanyFirmaGG(rucEmpresa);

      const workerContractData = buildWorkerContractData({
        worker: selectedWorker,
        template: selectedTemplate,
        usuario,
        rucEmpresa,
        assignmentData,
        companyData,
      });
      const contractHtml = buildContractHtmlFromTemplate({
        template: selectedTemplate,
        dataMap: workerContractData,
      });
      const assignmentRef = doc(collection(db, "contratoAsignaciones"));
      const empleadoRef = doc(db, "pl_empleados", workerDni);
      const userRef = doc(db, "users", workerDni);
      const batch = writeBatch(db);

      batch.set(
        empleadoRef,
        {
          contratoPlantillaId: payload.contratoPlantillaId,
          contratoPlantillaNombre: payload.contratoPlantillaNombre,
          contratoTipo: payload.contratoTipo,
          contratoFechaInicio: payload.contratoFechaInicio,
          contratoFechaFin: payload.contratoFechaFin,
          fecha_inicio_contrato: payload.fecha_inicio_contrato,
          fecha_fin_contrato: payload.fecha_fin_contrato,
          fecha_fin_periodo_prueba: payload.fecha_fin_periodo_prueba,
          sueldo_base: payload.sueldo_base,
          cargo: payload.cargo,
          contratoAsignadoAt: payload.contratoAsignadoAt,
          ...(payload.telefono ? { telefono: payload.telefono } : {}),
          ...(payload.correo ? { email: payload.correo } : {}),
        },
        { merge: true }
      );
      batch.set(
        userRef,
        {
          contratoPlantillaId: payload.contratoPlantillaId,
          contratoPlantillaNombre: payload.contratoPlantillaNombre,
          contratoTipo: payload.contratoTipo,
          fechaInicioContrato: payload.contratoFechaInicio,
          fechaFinContrato: payload.contratoFechaFin,
          sueldo_base: payload.sueldo_base,
          cargo: payload.cargo,
          contratoAsignadoAt: payload.contratoAsignadoAt,
        },
        { merge: true }
      );
      batch.set(assignmentRef, {
        ruc: rucEmpresa,
        ruc_empresa: rucEmpresa,
        trabajadorId: workerDni,
        trabajadorNombre: `${toSafeText(selectedWorker.apellidos)} ${toSafeText(selectedWorker.nombres)}`.trim(),
        contratoHtml: contractHtml,
        companyFirmaGG: firmaGGActual,
        templateSnapshot: {
          templateName: toSafeText(selectedTemplate.templateName),
          contractTitle: toSafeText(selectedTemplate.contractTitle || selectedTemplate.templateName),
          pdfMarginPreset: toSafeText(selectedTemplate.pdfMarginPreset || "NORMAL"),
          clauses: Array.isArray(selectedTemplate.clauses) ? selectedTemplate.clauses : [],
        },
        contractData: workerContractData,
        estado: "BORRADOR",
        ...payload,
        createdAt: serverTimestamp(),
      });

      await batch.commit();

      setAssignmentMessage("Plantilla asignada. Revisa y descarga el contrato desde la tabla inferior.");
      setAssignments((prev) => [
        {
          id: assignmentRef.id,
          ruc: rucEmpresa,
          ruc_empresa: rucEmpresa,
          trabajadorId: workerDni,
          trabajadorNombre: `${toSafeText(selectedWorker.apellidos)} ${toSafeText(selectedWorker.nombres)}`.trim(),
          contratoHtml: contractHtml,
          companyFirmaGG: firmaGGActual,
          templateSnapshot: {
            templateName: toSafeText(selectedTemplate.templateName),
            contractTitle: toSafeText(selectedTemplate.contractTitle || selectedTemplate.templateName),
            pdfMarginPreset: toSafeText(selectedTemplate.pdfMarginPreset || "NORMAL"),
            clauses: Array.isArray(selectedTemplate.clauses) ? selectedTemplate.clauses : [],
          },
          contractData: workerContractData,
          estado: "BORRADOR",
          ...payload,
          createdAt: new Date(),
          updatedAt: new Date(),
        },
        ...prev,
      ]);

      logBusinessAction("ASIGNACION_PLANTILLA_CONTRATO_TRABAJADOR", "GestionDocumentos", {
        plantillaId: selectedTemplate.id,
        plantillaNombre: selectedTemplate.templateName || "",
        trabajadorId: workerDni,
        fechaInicio: payload.contratoFechaInicio,
        fechaFin: payload.contratoFechaFin,
        sueldo: payload.sueldo_base,
        cargo: payload.cargo,
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de asignación", e));
    } catch (err) {
      console.error("Error asignando plantilla de contrato:", err);
      setAssignmentMessage("No se pudo asignar la plantilla. Intenta nuevamente.");
    } finally {
      setSavingAssignment(false);
    }
  };

  const downloadContractPreview = async (assignment) => {
    const worker = toSafeText(assignment?.trabajadorNombre || assignment?.trabajadorId || "trabajador").replace(/\s+/g, "_");
    const fileName = `Contrato_${worker}.pdf`;

    try {
      const firmaGGActual =
        toSafeText(assignment?.companyFirmaGG || companyData?.firmaGG) ||
        await fetchCompanyFirmaGG(rucEmpresa);

      const templateSnapshot = assignment?.templateSnapshot || {};
      const clauses = Array.isArray(templateSnapshot?.clauses) ? templateSnapshot.clauses : [];
      const contractData = assignment?.contractData || {};
      if (clauses.length) {
        const sections = buildContratoSections(
          clauses,
          contractData,
          toSafeText(templateSnapshot?.templateName || "CONTRATO DE TRABAJO"),
          {
            boldVariables: true,
            contractTitle: toSafeText(templateSnapshot?.contractTitle || templateSnapshot?.templateName || "CONTRATO DE TRABAJO"),
          }
        );
        await exportPdfLikeGenerador(
          sections,
          fileName,
          toSafeText(templateSnapshot?.pdfMarginPreset || "NORMAL"),
          firmaGGActual
        );
        return;
      }

      const html = toSafeText(assignment?.contratoHtml);
      if (!html) {
        setAssignmentMessage("La asignación no tiene vista previa de contrato.");
        return;
      }

      const employeeName = toSafeText(
        assignment?.contractData?.firma_trabajador_nombre || assignment?.trabajadorNombre
      );
      const employeeDni = toSafeText(
        assignment?.contractData?.firma_trabajador_dni || assignment?.trabajadorId
      );
      const employerName = toSafeText(
        assignment?.contractData?.firma_empleador_nombre ||
          assignment?.contractData?.representante_legal ||
          usuario?.representante_legal ||
          ""
      );
      const employerRole = toSafeText(
        assignment?.contractData?.firma_empleador_cargo ||
          usuario?.representante_legal_cargo ||
          ""
      );

      let firmaGGForLegacyHtml = "";
      if (firmaGGActual) {
        try {
          // html2canvas puede omitir imágenes remotas por timing/CORS; Data URL lo evita.
          firmaGGForLegacyHtml = await toDataUrlFromImageUrl(firmaGGActual);
        } catch (e) {
          console.warn("No se pudo preparar firmaGG para borrador legacy:", e);
          firmaGGForLegacyHtml = "";
        }
      }

      const firmaBlockHtml = `
        <div style="margin-top:64px; width:100%; font-family: 'Times New Roman', serif; font-size: 11px; color:#111;">
          <table style="width:100%; border-collapse:collapse; table-layout:fixed;">
            <tr>
              <td style="width:48%; text-align:center; vertical-align:bottom; padding-right:12px;">
                ${firmaGGForLegacyHtml ? `<img src="${firmaGGForLegacyHtml}" alt="Firma empleador" style="max-width:220px; max-height:70px; object-fit:contain; margin-bottom:4px;" />` : "<div style='height:74px;'></div>"}
                <div style="border-top:1px solid #333; margin-top:4px;"></div>
                <div style="margin-top:6px; font-weight:700;">EL EMPLEADOR</div>
                ${employerName ? `<div style="margin-top:4px;">${employerName}</div>` : ""}
                ${employerRole ? `<div style="margin-top:2px;">${employerRole}</div>` : ""}
              </td>
              <td style="width:4%;"></td>
              <td style="width:48%; text-align:center; vertical-align:bottom; padding-left:12px;">
                <div style='height:74px;'></div>
                <div style="border-top:1px solid #333; margin-top:4px;"></div>
                <div style="margin-top:6px; font-weight:700;">EL TRABAJADOR</div>
                ${employeeName ? `<div style="margin-top:4px;">${employeeName}</div>` : ""}
                ${employeeDni ? `<div style="margin-top:2px;">DNI: ${employeeDni}</div>` : ""}
              </td>
            </tr>
          </table>
        </div>
      `;

      const wrapper = document.createElement("div");
      wrapper.style.position = "fixed";
      wrapper.style.top = "0";
      wrapper.style.left = "0";
      wrapper.style.width = "794px";
      wrapper.style.minHeight = "1123px";
      wrapper.style.padding = "0";
      wrapper.style.background = "#fff";
      wrapper.style.color = "#111";
      wrapper.style.zIndex = "-1";
      wrapper.style.opacity = "0.01";
      wrapper.style.pointerEvents = "none";
      wrapper.innerHTML = `${html}${firmaBlockHtml}`;
      document.body.appendChild(wrapper);
      try {
        const pdf = new jsPDF({ unit: "pt", format: "a4" });
        await pdf.html(wrapper, {
          margin: [28, 28, 28, 28],
          autoPaging: "text",
          html2canvas: {
            scale: 1,
            backgroundColor: "#ffffff",
            useCORS: true,
            logging: false,
            windowWidth: 794,
          },
        });
        pdf.save(fileName);
      } finally {
        if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
      }
    } catch (error) {
      console.error("Error generando PDF de borrador:", error);
      setAssignmentMessage("No se pudo generar el PDF del borrador.");
    }
  };

  const handleDelete = async (template) => {
    const templateName = toSafeText(template?.templateName) || "Sin nombre";
    const shouldDelete = window.confirm(`¿Eliminar la plantilla "${templateName}"?`);
    if (!shouldDelete) return;

    try {
      await deleteDoc(doc(db, "contratoPlantillas", template.id));
      await loadTemplates();

      // AUDITORÍA
      try {
        await logBusinessAction("ELIMINACION_PLANTILLA_CONTRATO", "GestionDocumentos", {
          templateName: templateName,
          contractType: template.contractType || "GENERAL",
          plantillaId: template.id
        }, usuario);
      } catch (e) {
        console.warn("No se pudo registrar la auditoría de eliminación", e);
      }
    } catch (err) {
      console.error("Error eliminando plantilla:", err);
      setError("No se pudo eliminar la plantilla seleccionada.");
    }
  };

  const handleDeleteAssignment = async (assignment) => {
    const workerName = toSafeText(assignment?.trabajadorNombre || assignment?.trabajadorId) || "trabajador";
    const shouldDelete = window.confirm(`¿Eliminar el borrador de contrato de "${workerName}"?`);
    if (!shouldDelete) return;
    try {
      await deleteDoc(doc(db, "contratoAsignaciones", assignment.id));
      await loadAssignments();
      setAssignmentMessage("Borrador eliminado correctamente.");
      logBusinessAction("ELIMINACION_BORRADOR_CONTRATO", "GestionDocumentos", {
        contratoAsignacionId: assignment?.id || "",
        trabajadorId: toSafeText(assignment?.trabajadorId),
        trabajadorNombre: workerName,
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de eliminación de borrador", e));
    } catch (err) {
      console.error("Error eliminando borrador de contrato:", err);
      setAssignmentMessage("No se pudo eliminar el borrador.");
      logBusinessAction("ERROR_ELIMINACION_BORRADOR_CONTRATO", "GestionDocumentos", {
        contratoAsignacionId: assignment?.id || "",
        trabajadorId: toSafeText(assignment?.trabajadorId),
        error: toSafeText(err?.message),
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de error al eliminar borrador", e));
    }
  };

  const handleSendAssignment = async (assignment) => {
    if (isAssignmentAlreadySent(assignment)) {
      setAssignmentMessage("Este contrato ya fue enviado previamente. Usa el estado de firma para seguimiento.");
      return;
    }

    const contratoFechaFin = toSafeText(assignment?.contratoFechaFin || assignment?.fecha_fin_contrato);
    const trabajadorId = toSafeText(assignment?.trabajadorId);
    if (!trabajadorId) {
      setAssignmentMessage("No se encontró el trabajador de esta asignación.");
      return;
    }
    if (!contratoFechaFin) {
      setAssignmentMessage("Para enviar, debes definir la fecha de fin de contrato.");
      return;
    }

    try {
      const assignmentRuc = toSafeText(assignment?.ruc || assignment?.ruc_empresa || rucEmpresa);
      const firmaGGDataUrl = await resolveFirmaGGDataUrl({
        assignmentRuc,
        companyFirmaGG: toSafeText(companyData?.firmaGG),
        assignmentFirmaGG: toSafeText(assignment?.companyFirmaGG),
      });

      const trabajadorSnap = await getDoc(doc(db, "users", trabajadorId));
      const trabajadorData = trabajadorSnap.exists() ? trabajadorSnap.data() : {};
      const templateSnapshot = assignment?.templateSnapshot || {};
      const clauses = Array.isArray(templateSnapshot?.clauses) ? templateSnapshot.clauses : [];
      const contractData = assignment?.contractData || {};
      if (!clauses.length) {
        setAssignmentMessage("No se encontró contenido de plantilla para generar el PDF.");
        return;
      }

      const sections = buildContratoSections(
        clauses,
        contractData,
        toSafeText(templateSnapshot?.templateName || "CONTRATO DE TRABAJO"),
        {
          boldVariables: true,
          contractTitle: toSafeText(templateSnapshot?.contractTitle || templateSnapshot?.templateName || "CONTRATO DE TRABAJO"),
        }
      );

      const tituloDocumento = sanitizeTitleForFileName(templateSnapshot?.templateName || "Contrato de trabajo");
      const uniqueSuffix = buildUniqueStorageSuffix();
      const fileName = `${trabajadorId}-${tituloDocumento}-${uniqueSuffix}.pdf`;
      const pdfBlob = await exportPdfLikeGenerador(
        sections,
        fileName,
        toSafeText(templateSnapshot?.pdfMarginPreset || "NORMAL"),
        firmaGGDataUrl,
        { download: false }
      );
      if (!pdfBlob) {
        setAssignmentMessage("No se pudo generar el PDF para envío.");
        return;
      }

      const ruc = assignmentRuc || rucEmpresa;
      const onboardingDocsRef = collection(db, `onboarding_lista/${ruc}/documentos`);
      const onboardingDocsSnap = await getDocs(onboardingDocsRef);
      let selectedDoc = null;
      const plantillaNombre = toSafeText(assignment?.contratoPlantillaNombre || templateSnapshot?.templateName).toLowerCase();
      const preferredTitle = plantillaNombre.includes("adenda") ? "Adenda Contrato" : "Contratos";

      onboardingDocsSnap.forEach((d) => {
        const data = d.data() || {};
        const title = toSafeText(data.title).toLowerCase();
        if (selectedDoc) return;
        if (data.type === "sign" && data.activo === true && title === preferredTitle.toLowerCase()) {
          selectedDoc = {
            id: d.id,
            name: toSafeText(data.title) || "Contrato de trabajo",
            type: data.type || "sign",
            obligatorio: data.obligatorio || false,
          };
        }
      });
      if (!selectedDoc) {
        onboardingDocsSnap.forEach((d) => {
          const data = d.data() || {};
          const title = toSafeText(data.title).toLowerCase();
          if (selectedDoc) return;
          if (data.type === "sign" && data.activo === true && title.includes("contrato")) {
            selectedDoc = {
              id: d.id,
              name: toSafeText(data.title) || preferredTitle,
              type: data.type || "sign",
              obligatorio: data.obligatorio || false,
            };
          }
        });
      }
      if (!selectedDoc) {
        selectedDoc = { id: "contrato_trabajo", name: preferredTitle, type: "sign", obligatorio: false };
      }

      const storageRef = ref(storage, `onboarding/${trabajadorId}/${selectedDoc.name}/${fileName}`);
      const uploadResult = await uploadBytes(storageRef, pdfBlob, { contentType: "application/pdf" });
      const downloadURL = await getDownloadURL(uploadResult.ref);

      await writeBatch(db)
        .set(
          doc(db, `onboarding/${trabajadorId}/documentos`, selectedDoc.id),
          {
            id: selectedDoc.id,
            title: selectedDoc.name,
            type: selectedDoc.type || "sign",
            obligatorio: selectedDoc.obligatorio || false,
            activo: true,
            completed: false,
            asignadoPor: usuario.email || usuario.nombre,
            asignadoEl: serverTimestamp(),
          },
          { merge: true }
        )
        .commit();

      // El ID se genera en el cliente para escribir el documento y su campo
      // `id` en una sola operación (antes: addDoc + writeBatch.set = 2 viajes).
      const legajoRef = doc(collection(db, "onboarding", trabajadorId, "legajo"));
      await setDoc(legajoRef, {
        id: legajoRef.id,
        activo: true,
        ano: new Date().getFullYear(),
        contador: 0,
        dni: trabajadorId,
        fechaDoc: serverTimestamp(),
        fechaVista: "",
        link: downloadURL,
        mes: String(new Date().getMonth() + 1).padStart(2, "0"),
        nombre: toSafeText(templateSnapshot?.templateName || selectedDoc.name).toUpperCase(),
        ruc: ruc,
        tipo: selectedDoc.name,
        procesadoPor: usuario.email || usuario.nombre || "",
        documentId: selectedDoc.id,
      });

      const nombreEmpresa = toSafeText(companyData?.nombreComercial || companyData?.name || usuario?.empresa || "");
      const correo = toSafeText(trabajadorData?.email);
      const cc = toSafeText(trabajadorData?.recursosHumanos || usuario?.recursosHumanos);
      const onboardingTitle = toSafeText(selectedDoc?.name).toLowerCase();
      const documentoCorporativo = onboardingTitle.includes("adenda")
        ? "Adenda al Contrato de Trabajo"
        : "Contrato de Trabajo";
      const notificacionesHabilitadas = companyData?.notificaciones === true;
      if (notificacionesHabilitadas) {
        const primerNombre = toSafeText(trabajadorData?.name).split(" ")[0] || "Colaborador";
        const textNotificacion = `${primerNombre}, se le ha asignado un(a) ${documentoCorporativo} pendiente de firma.`;
        const notificacion = {
          dni: trabajadorId,
          agentId: "comunication-agent",
          data: {
            isUser: false,
            read: false,
            text: textNotificacion,
          },
        };
        try {
          await sendNotificationBatch([notificacion]);
        } catch (notifyError) {
          console.warn("No se pudo enviar notificación push de contrato:", notifyError);
        }
      }
      if (correo) {
        const asunto = `${nombreEmpresa || "Empresa"} - ${documentoCorporativo} para firma`;
        const body =
          `Estimado(a) ${toSafeText(trabajadorData?.name)} ${toSafeText(trabajadorData?.apellidos)},\n\n` +
          `Se le ha asignado un(a) ${documentoCorporativo} para su firma.\n` +
          `Por favor ingrese al aplicativo Mi Boleta Gestión de Onboarding para revisarlo.\n\n` +
          `Saludos cordiales,\n${nombreEmpresa || "Mi Boleta"}`;
        await sendCustomEmail(correo, cc || null, "miboleta@perubi.com", asunto, body, toSafeText(companyData?.coleccionEmail) || "mailTurbo");
      }

      const batch = writeBatch(db);
      batch.set(
        doc(db, "contratoAsignaciones", assignment.id),
        {
          estado: "ENVIADO",
          enviadoAt: serverTimestamp(),
          enviadoPor: toSafeText(usuario?.email),
          companyFirmaGG: toSafeText(companyData?.firmaGG) || toSafeText(assignment?.companyFirmaGG) || "",
          onboardingLink: downloadURL,
          onboardingDocumentId: selectedDoc.id,
          onboardingLegajoId: legajoRef.id,
        },
        { merge: true }
      );
      await batch.commit();

      setAssignments((prev) =>
        prev.map((item) =>
          item.id === assignment.id
            ? {
              ...item,
              estado: "ENVIADO",
              enviadoPor: toSafeText(usuario?.email),
              contratoFechaFin,
              onboardingLink: downloadURL,
              onboardingDocumentId: selectedDoc.id,
              onboardingLegajoId: legajoRef.id,
              firmaEstado: "PENDIENTE",
              firmaCompletado: false,
              firmaFecha: null,
              linkFirmado: "",
            }
            : item
        )
      );
      setSendResults((prev) => [
        {
          id: `${assignment.id}_${Date.now()}`,
          estado: "ENVIADO",
          trabajador: toSafeText(assignment?.trabajadorNombre) || trabajadorId,
          documento: toSafeText(templateSnapshot?.templateName || "CONTRATO"),
          onboardingTitle: toSafeText(selectedDoc?.name),
          legajoTipo: toSafeText(selectedDoc?.name),
          link: downloadURL,
          fecha: new Date(),
          detalle: "Enviado por onboarding correctamente.",
        },
        ...prev,
      ]);
      setAssignmentMessage("Contrato enviado al trabajador por onboarding. Actualiza fechas cuando figure firmado.");
      logBusinessAction("ENVIO_CONTRATO_TRABAJADOR", "GestionDocumentos", {
        contratoAsignacionId: assignment.id,
        trabajadorId,
        fechaFinContrato: contratoFechaFin,
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de envío", e));
    } catch (err) {
      console.error("Error enviando contrato:", err);
      setSendResults((prev) => [
        {
          id: `${assignment?.id || "sin_id"}_${Date.now()}`,
          estado: "ERROR",
          trabajador: toSafeText(assignment?.trabajadorNombre) || toSafeText(assignment?.trabajadorId) || "-",
          documento: toSafeText(assignment?.contratoPlantillaNombre) || "-",
          onboardingTitle: "-",
          legajoTipo: "-",
          link: "",
          fecha: new Date(),
          detalle: toSafeText(err?.message) || "No se pudo enviar el contrato.",
        },
        ...prev,
      ]);
      setAssignmentMessage("No se pudo enviar el contrato.");
    }
  };

  const handleSendMassiveAssignments = async () => {
    const borradoresVisibles = filteredAssignmentsList.filter(
      (item) => !isAssignmentAlreadySent(item)
    );
    if (!borradoresVisibles.length) {
      setAssignmentMessage("No hay borradores pendientes para envío masivo.");
      return;
    }
    const shouldSend = window.confirm(
      `¿Enviar ${borradoresVisibles.length} borrador(es) al onboarding de los trabajadores?`
    );
    if (!shouldSend) return;

    try {
      setSendingMassive(true);
      for (const assignment of borradoresVisibles) {
        // Reutiliza el flujo individual para mantener el mismo comportamiento (PDF, onboarding, correo, push y vencimientos)
        // eslint-disable-next-line no-await-in-loop
        await handleSendAssignment(assignment);
      }
      setAssignmentMessage(`Envío masivo finalizado. Se procesaron ${borradoresVisibles.length} borrador(es).`);
      logBusinessAction("ENVIO_MASIVO_CONTRATOS", "GestionDocumentos", {
        totalProcesados: borradoresVisibles.length,
        contratoAsignacionIds: borradoresVisibles.map((x) => toSafeText(x?.id)).filter(Boolean),
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de envío masivo", e));
    } finally {
      setSendingMassive(false);
    }
  };

  const updateEmployeeContractDates = async (assignment) => {
    const trabajadorId = toSafeText(assignment?.trabajadorId);
    const contratoFechaInicio = toSafeText(assignment?.contratoFechaInicio || assignment?.fecha_inicio_contrato);
    const contratoFechaFin = toSafeText(assignment?.contratoFechaFin || assignment?.fecha_fin_contrato);

    if (!trabajadorId) {
      throw new Error("No se encontró el trabajador de la asignación.");
    }
    if (!contratoFechaInicio || !contratoFechaFin) {
      throw new Error("El contrato debe tener fecha de inicio y fin para actualizar la ficha.");
    }

    const batch = writeBatch(db);
    batch.set(
      doc(db, "users", trabajadorId),
      {
        fechaInicioContrato: contratoFechaInicio,
        fechaFinContrato: contratoFechaFin,
        vencimientoContrato: contratoFechaFin,
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
    batch.set(
      doc(db, "pl_empleados", trabajadorId),
      {
        fecha_inicio_contrato: contratoFechaInicio,
        fecha_fin_contrato: contratoFechaFin,
        vencimientoContrato: contratoFechaFin,
        updated_at: serverTimestamp(),
      },
      { merge: true }
    );
    await batch.commit();
  };

  const handleUpdateDatesForAssignment = async (assignment) => {
    const confirmResult = await Swal.fire({
      title: "Actualizar ficha del empleado",
      text: "Se actualizara la fecha de inicio y fin del contrato en la ficha del empleado. ¿Deseas continuar?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Si, actualizar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    if (!confirmResult.isConfirmed) return;

    if (!isAssignmentSigned(assignment)) {
      setAssignmentMessage("Solo puedes actualizar fechas de contratos firmados.");
      logBusinessAction("INTENTO_ACTUALIZAR_FECHAS_CONTRATO_NO_FIRMADO", "GestionDocumentos", {
        contratoAsignacionId: assignment?.id || "",
        trabajadorId: toSafeText(assignment?.trabajadorId),
        firmaEstado: toSafeText(assignment?.firmaEstado),
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de intento no firmado", e));
      return;
    }

    try {
      setUpdatingDates(true);
      await updateEmployeeContractDates(assignment);
      setAssignmentMessage("Fechas actualizadas en la ficha del empleado.");
      logBusinessAction("ACTUALIZACION_FECHAS_CONTRATO_FICHA", "GestionDocumentos", {
        contratoAsignacionId: assignment?.id || "",
        trabajadorId: toSafeText(assignment?.trabajadorId),
        fechaInicio: toSafeText(assignment?.contratoFechaInicio || assignment?.fecha_inicio_contrato),
        fechaFin: toSafeText(assignment?.contratoFechaFin || assignment?.fecha_fin_contrato),
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de actualización de fechas", e));
      await loadWorkers();
    } catch (err) {
      console.error("Error actualizando fechas de contrato en ficha:", err);
      setAssignmentMessage(toSafeText(err?.message) || "No se pudieron actualizar las fechas en la ficha del empleado.");
      logBusinessAction("ERROR_ACTUALIZACION_FECHAS_CONTRATO_FICHA", "GestionDocumentos", {
        contratoAsignacionId: assignment?.id || "",
        trabajadorId: toSafeText(assignment?.trabajadorId),
        error: toSafeText(err?.message),
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de error al actualizar fechas", e));
    } finally {
      setUpdatingDates(false);
    }
  };

  const handleUpdateDatesMassive = async () => {
    const signedAssignments = filteredAssignmentsList.filter((item) => isAssignmentSigned(item));
    if (!signedAssignments.length) {
      setAssignmentMessage("No hay contratos firmados para actualizar fechas.");
      return;
    }

    const confirmResult = await Swal.fire({
      title: "Actualizar ficha de empleados",
      text: `Se actualizara la fecha de inicio y fin para ${signedAssignments.length} contrato(s) firmado(s). ¿Deseas continuar?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Si, actualizar",
      cancelButtonText: "Cancelar",
      reverseButtons: true,
    });
    if (!confirmResult.isConfirmed) return;

    let success = 0;
    let failed = 0;

    try {
      setUpdatingDatesMassive(true);
      for (const assignment of signedAssignments) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await updateEmployeeContractDates(assignment);
          success += 1;
        } catch (e) {
          failed += 1;
          console.warn("No se pudieron actualizar fechas para asignación:", assignment?.id, e);
        }
      }
      setAssignmentMessage(`Actualización masiva completada. Correctos: ${success}. Fallidos: ${failed}.`);
      logBusinessAction("ACTUALIZACION_MASIVA_FECHAS_CONTRATOS_FIRMADOS", "GestionDocumentos", {
        totalFirmadosEvaluados: signedAssignments.length,
        totalActualizados: success,
        totalFallidos: failed,
        contratoAsignacionIds: signedAssignments.map((x) => toSafeText(x?.id)).filter(Boolean),
      }, usuario).catch((e) => console.warn("No se pudo registrar auditoría de actualización masiva", e));
      await loadWorkers();
    } finally {
      setUpdatingDatesMassive(false);
    }
  };

  return (
    <div className="min-vh-100" style={{ background: "#f5f7fb", padding: "20px 0" }}>
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader usuario={usuario} />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaFileSignature className="me-3" />
                Contratos Plantillas
              </h2>
              <p className="mb-0 opacity-75">
                Gestiona y reutiliza plantillas de contratos por empresa
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {error && <Alert variant="warning">{error}</Alert>}

            <Tabs defaultActiveKey="trazabilidad" id="contratos-main-tabs" className="mb-4 custom-tabs-contratos nav-pills">
              {/* PESTAÑA 1: SEGUIMIENTO Y FIRMAS */}
              <Tab eventKey="trazabilidad" title={<span>📊 Seguimiento y Firmas</span>}>
                <Row className="mb-4">
                  <Col md={3}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                      <Card.Body
                        className="text-center"
                        style={{
                          background: "linear-gradient(135deg, #0d4f3c 0%, #1e7e34 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <h4 className="fw-bold">{assignments.length}</h4>
                        <p className="mb-0 small">Asignados Totales</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                      <Card.Body
                        className="text-center"
                        style={{
                          background: "linear-gradient(135deg, #1e7e34 0%, #20c997 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <h4 className="fw-bold">{assignments.filter(isAssignmentSigned).length}</h4>
                        <p className="mb-0 small">Firmados</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                      <Card.Body
                        className="text-center"
                        style={{
                          background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <h4 className="fw-bold">{assignments.filter(item => isAssignmentAlreadySent(item) && !isAssignmentSigned(item)).length}</h4>
                        <p className="mb-0 small">Pendientes de Firma</p>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={3}>
                    <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                      <Card.Body
                        className="text-center"
                        style={{
                          background: "linear-gradient(135deg, #6c757d 0%, #495057 100%)",
                          color: "white",
                          borderRadius: "15px",
                        }}
                      >
                        <h4 className="fw-bold">{assignments.filter(item => !isAssignmentAlreadySent(item)).length}</h4>
                        <p className="mb-0 small">Borradores (Sin Enviar)</p>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <FaSearch className="me-2" />
                      <div>
                        <h5 className="mb-0 fw-semibold">Filtros de Seguimiento</h5>
                        <small className="opacity-75">Busca por trabajador, mes o año de inicio del contrato</small>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Buscar Trabajador / DNI</Form.Label>
                          <Form.Control
                            value={searchAssignment}
                            onChange={(e) => setSearchAssignment(e.target.value)}
                            placeholder="Nombre, apellidos o DNI..."
                            style={{ borderRadius: "10px" }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Mes de Inicio</Form.Label>
                          <Form.Select
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                            style={{ borderRadius: "10px" }}
                          >
                            <option value="TODOS">Todos los meses</option>
                            <option value="1">Enero</option>
                            <option value="2">Febrero</option>
                            <option value="3">Marzo</option>
                            <option value="4">Abril</option>
                            <option value="5">Mayo</option>
                            <option value="6">Junio</option>
                            <option value="7">Julio</option>
                            <option value="8">Agosto</option>
                            <option value="9">Septiembre</option>
                            <option value="10">Octubre</option>
                            <option value="11">Noviembre</option>
                            <option value="12">Diciembre</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Año de Inicio</Form.Label>
                          <Form.Select
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                            style={{ borderRadius: "10px" }}
                          >
                            <option value="TODOS">Todos los años</option>
                            {availableYears.map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={2} className="d-flex align-items-end">
                        <Button
                          variant="outline-secondary"
                          className="w-100"
                          onClick={() => {
                            setSearchAssignment("");
                            setFilterMonth("TODOS");
                            setFilterYear("TODOS");
                          }}
                          style={{ borderRadius: "10px" }}
                        >
                          Limpiar
                        </Button>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <h5 className="mb-0 fw-semibold">Contratos Asignados (Revisión y Seguimiento)</h5>
                      <Stack direction="horizontal" gap={2}>
                        <Button
                          size="sm"
                          variant="outline-light"
                          onClick={handleSendMassiveAssignments}
                          disabled={sendingMassive || loadingAssignments || pendingAssignmentsCount === 0}
                        >
                          {sendingMassive ? <Spinner size="sm" className="me-1" /> : <FaPaperPlane className="me-1" />}
                          Enviar borradores ({pendingAssignmentsCount})
                        </Button>
                        <Button
                          size="sm"
                          variant="outline-light"
                          onClick={handleUpdateDatesMassive}
                          disabled={updatingDatesMassive || loadingAssignments || signedAssignmentsCount === 0}
                        >
                          {updatingDatesMassive ? <Spinner size="sm" className="me-1" /> : <FaSyncAlt className="me-1" />}
                          Actualizar fecha inicio/fin ({signedAssignmentsCount})
                        </Button>
                        <Button
                          size="sm"
                          variant={assignmentStatusFilter === "TODOS" ? "light" : "outline-light"}
                          onClick={() => setAssignmentStatusFilter("TODOS")}
                        >
                          Todos
                        </Button>
                        <Button
                          size="sm"
                          variant={assignmentStatusFilter === "BORRADOR" ? "warning" : "outline-light"}
                          onClick={() => setAssignmentStatusFilter("BORRADOR")}
                        >
                          Borrador
                        </Button>
                        <Button
                          size="sm"
                          variant={assignmentStatusFilter === "ENVIADO" ? "success" : "outline-light"}
                          onClick={() => setAssignmentStatusFilter("ENVIADO")}
                        >
                          Enviado
                        </Button>
                        <Button
                          size="sm"
                          variant={assignmentStatusFilter === "FIRMADO" ? "info" : "outline-light"}
                          onClick={() => setAssignmentStatusFilter("FIRMADO")}
                        >
                          Firmado
                        </Button>
                      </Stack>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {loadingAssignments ? (
                      <div className="p-4 text-center">
                        <Spinner size="sm" className="me-2" />
                        Cargando borradores...
                      </div>
                    ) : filteredAssignmentsList.length === 0 ? (
                      <div className="p-4 text-muted text-center">No se encontraron contratos asignados con los filtros seleccionados.</div>
                    ) : (
                      <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                        <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                          <thead
                            style={{
                              backgroundColor: "#f8f9fa",
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                            }}
                          >
                            <tr>
                              <th className="text-secondary fw-semibold py-3 px-3">Trabajador</th>
                              <th className="text-secondary fw-semibold py-3 px-3">Plantilla</th>
                              <th className="text-secondary fw-semibold py-3 px-3 text-center">Inicio</th>
                              <th className="text-secondary fw-semibold py-3 px-3 text-center">Fin</th>
                              <th className="text-secondary fw-semibold py-3 px-3 text-end">Salario</th>
                              <th className="text-secondary fw-semibold py-3 px-3">Puesto</th>
                              <th className="text-secondary fw-semibold py-3 px-3 text-center">Estado Envío</th>
                              <th className="text-secondary fw-semibold py-3 px-3 text-center">Firma Trabajador</th>
                              <th className="text-secondary fw-semibold py-3 px-3 text-center">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredAssignmentsList.map((item) => (
                              <tr key={item.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                                <td className="py-3 px-3 fw-semibold text-dark">
                                  {toSafeText(item.trabajadorNombre) || toSafeText(item.trabajadorId)}
                                  <div className="small text-muted">DNI: {toSafeText(item.trabajadorId)}</div>
                                </td>
                                <td className="py-3 px-3">{toSafeText(item.contratoPlantillaNombre) || "-"}</td>
                                <td className="text-center py-3 px-3">{toSafeText(item.contratoFechaInicio) || "-"}</td>
                                <td className="text-center py-3 px-3">{toSafeText(item.contratoFechaFin) || "-"}</td>
                                <td className="text-end py-3 px-3 fw-bold text-success">S/ {Number(item.sueldo_base || 0).toFixed(2)}</td>
                                <td className="py-3 px-3">{toSafeText(item.cargo) || "-"}</td>
                                <td className="text-center py-3 px-3">
                                  <Badge bg={toSafeText(item.estado) === "ENVIADO" ? "success" : "warning"} text={toSafeText(item.estado) === "ENVIADO" ? "light" : "dark"}>
                                    {toSafeText(item.estado) || "BORRADOR"}
                                  </Badge>
                                </td>
                                <td className="text-center py-3 px-3">
                                  {toSafeText(item.firmaEstado) === "FIRMADO" ? (
                                    <>
                                      <Badge bg="success" className="d-block mb-1">Firmado</Badge>
                                      {item.firmaFecha ? <span className="small text-muted">{formatTimestamp(item.firmaFecha)}</span> : null}
                                    </>
                                  ) : toSafeText(item.firmaEstado) === "PENDIENTE" ? (
                                    <Badge bg="warning" text="dark" className="d-block">Pendiente</Badge>
                                  ) : toSafeText(item.firmaEstado) === "NO_ENVIADO" ? (
                                    <Badge bg="secondary" className="d-block">No enviado</Badge>
                                  ) : (
                                    <Badge bg="light" text="dark" className="d-block">Sin dato</Badge>
                                  )}
                                </td>
                                <td className="text-center py-3 px-3">
                                  <Stack direction="horizontal" gap={1} className="justify-content-center">
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Enviar contrato al trabajador</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant={isAssignmentAlreadySent(item) ? "outline-success" : "outline-dark"}
                                        onClick={() => handleSendAssignment(item)}
                                        style={{ borderRadius: "8px" }}
                                        disabled={isAssignmentAlreadySent(item) || sendingMassive}
                                      >
                                        <FaPaperPlane size="13px" />
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Descargar PDF borrador</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => downloadContractPreview(item)}
                                        style={{ borderRadius: "8px" }}
                                      >
                                        <FaDownload size="13px" />
                                      </Button>
                                    </OverlayTrigger>
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Actualizar fechas en ficha de empleado (solo firmado)</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant={isAssignmentSigned(item) ? "outline-info" : "outline-secondary"}
                                        onClick={() => handleUpdateDatesForAssignment(item)}
                                        disabled={!isAssignmentSigned(item) || updatingDates || updatingDatesMassive}
                                        style={{ borderRadius: "8px" }}
                                      >
                                        <FaCalendarAlt size="13px" />
                                      </Button>
                                    </OverlayTrigger>
                                    {toSafeText(item.linkFirmado) ? (
                                      <OverlayTrigger placement="top" overlay={<Tooltip>Ver documento firmado</Tooltip>}>
                                        <Button
                                          as="a"
                                          size="sm"
                                          variant="outline-success"
                                          href={item.linkFirmado}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ borderRadius: "8px" }}
                                        >
                                          <FaFileSignature size="13px" />
                                        </Button>
                                      </OverlayTrigger>
                                    ) : null}
                                    <OverlayTrigger placement="top" overlay={<Tooltip>Eliminar borrador</Tooltip>}>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => handleDeleteAssignment(item)}
                                        style={{ borderRadius: "8px" }}
                                      >
                                        <FaTrash size="13px" />
                                      </Button>
                                    </OverlayTrigger>
                                  </Stack>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* PESTAÑA 2: ASIGNAR CONTRATO */}
              <Tab eventKey="asignar" title={<span>✍️ Asignar Contrato</span>}>
                <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <h5 className="mb-0 fw-semibold">Asignar Plantilla a Trabajador</h5>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Plantilla de contrato</Form.Label>
                          <Form.Select
                            value={selectedTemplateId}
                            onChange={(e) => pickTemplateForAssignment(e.target.value)}
                          >
                            <option value="">Selecciona una plantilla</option>
                            {templates.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.templateName || "Sin nombre"} · {item.contractType || "GENERAL"}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Trabajador</Form.Label>
                          <Form.Select
                            value={selectedWorkerId}
                            onChange={(e) => setSelectedWorkerId(e.target.value)}
                            disabled={loadingWorkers}
                          >
                            {!workers.length ? (
                              <option value="">Sin trabajadores disponibles</option>
                            ) : (
                              workers.map((worker) => (
                                <option key={worker.id} value={worker.id}>
                                  {`${toSafeText(worker.apellidos)} ${toSafeText(worker.nombres)}`.trim() || "Sin nombre"} · DNI {toSafeText(worker.dni || worker.id)}
                                </option>
                              ))
                            )}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Inicio de contrato</Form.Label>
                          <Form.Control
                            type="date"
                            value={assignmentData.fechaInicio}
                            onChange={(e) => {
                              const nextInicio = e.target.value;
                              const nextFinPrueba = nextInicio ? calcularFinPeriodoPrueba(nextInicio, assignmentData.periodo_prueba_meses || 3) : "";
                              setAssignmentData((prev) => ({
                                ...prev,
                                fechaInicio: nextInicio,
                                fecha_fin_periodo_prueba: nextFinPrueba ? formatDateEsPe(nextFinPrueba) : prev.fecha_fin_periodo_prueba,
                              }));
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Fin de contrato</Form.Label>
                          <Form.Control
                            type="date"
                            value={assignmentData.fechaFin}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, fechaFin: e.target.value }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Salario (S/)</Form.Label>
                          <Form.Control
                            type="number"
                            min="0"
                            step="0.01"
                            value={assignmentData.salario}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, salario: e.target.value }))}
                            placeholder="Ej: 1800"
                          />
                          {assignmentData.salario ? (
                            <small className="text-muted d-block mt-1">
                              En contrato: {formatSoles(assignmentData.salario)}
                            </small>
                          ) : null}
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Puesto</Form.Label>
                          <Form.Control
                            value={assignmentData.puesto}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, puesto: e.target.value }))}
                            placeholder="Ej: Asistente administrativo"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Fin Periodo Prueba</Form.Label>
                          <Form.Control
                            value={assignmentData.fecha_fin_periodo_prueba || (assignmentData.fechaInicio ? formatDateEsPe(calcularFinPeriodoPrueba(assignmentData.fechaInicio)) : "")}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, fecha_fin_periodo_prueba: e.target.value }))}
                            placeholder="DD/MM/YYYY"
                          />
                          <small className="text-muted d-block mt-1">
                            +3 meses auto
                          </small>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Teléfono</Form.Label>
                          <Form.Control
                            value={assignmentData.telefono || ""}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, telefono: e.target.value }))}
                            placeholder="Ej: 987654321"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Correo del trabajador</Form.Label>
                          <Form.Control
                            type="email"
                            value={assignmentData.correo || ""}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, correo: e.target.value }))}
                            placeholder="correo@ejemplo.com"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Representante Legal (Empleador)</Form.Label>
                          <Form.Control
                            value={assignmentData.representante_legal || ""}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, representante_legal: e.target.value }))}
                            placeholder="Nombre del representante legal"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Cargo Representante Legal</Form.Label>
                          <Form.Control
                            value={assignmentData.representante_legal_cargo || ""}
                            onChange={(e) => setAssignmentData((prev) => ({ ...prev, representante_legal_cargo: e.target.value }))}
                            placeholder="Ej: Gerente General"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Stack direction="horizontal" gap={2} className="mt-3">
                      <Button
                        variant="success"
                        onClick={handleAssignTemplate}
                        disabled={savingAssignment || loadingWorkers}
                        style={{ borderRadius: "10px" }}
                      >
                        {savingAssignment ? <Spinner size="sm" className="me-1" /> : null}
                        Asignar plantilla
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={loadWorkers}
                        disabled={loadingWorkers}
                        style={{ borderRadius: "10px" }}
                      >
                        {loadingWorkers ? <Spinner size="sm" className="me-1" /> : null}
                        Actualizar trabajadores
                      </Button>
                    </Stack>
                    {assignmentMessage && (
                      <Alert variant={assignmentMessage.includes("correctamente") || assignmentMessage.includes("asignada") ? "success" : "warning"} className="mt-3 mb-0">
                        {assignmentMessage}
                      </Alert>
                    )}
                  </Card.Body>
                </Card>
              </Tab>

              {/* PESTAÑA 3: GESTIONAR PLANTILLAS */}
              <Tab eventKey="plantillas" title={<span>📋 Gestionar Plantillas</span>}>
                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Body className="p-4">
                    <Row className="align-items-center">
                      <Col md={9}>
                        <h5 className="mb-1 fw-semibold">Ficha de Empresa</h5>
                        <p className="text-muted mb-0 small">
                          Empresa {usuario?.empresa || usuario?.empresaNombreComercial || "Sin nombre"} · RUC {rucEmpresa || "Sin RUC"}
                        </p>
                      </Col>
                      <Col md={3} className="text-end">
                        <Badge bg="dark" style={{ fontSize: "0.9rem" }}>
                          {filteredTemplates.length} plantilla(s)
                        </Badge>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <div className="d-flex align-items-center">
                      <FaSearch className="me-2" />
                      <div>
                        <h5 className="mb-0 fw-semibold">Filtros y Acciones de Plantilla</h5>
                        <small className="opacity-75">Busca plantillas y crea nuevas</small>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Row className="g-3 align-items-end">
                      <Col md={8}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Buscar plantilla</Form.Label>
                          <Form.Control
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Nombre, tipo o creador..."
                            className="form-control-lg"
                            style={{ borderRadius: "10px" }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Stack direction="horizontal" gap={2} className="justify-content-md-end">
                          <OverlayTrigger placement="top" overlay={<Tooltip>Recargar plantillas</Tooltip>}>
                            <Button
                              variant="outline-secondary"
                              onClick={loadTemplates}
                              disabled={loading}
                              className="d-flex align-items-center gap-2"
                              style={{ borderRadius: "10px" }}
                            >
                              {loading ? <Spinner size="sm" className="me-1" /> : <FaSyncAlt size="16px" />}
                              Actualizar
                            </Button>
                          </OverlayTrigger>
                          <Button
                            variant="primary"
                            onClick={() => goToGenerator("")}
                            className="d-flex align-items-center gap-2"
                            style={{
                              borderRadius: "10px",
                              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                              border: "none",
                            }}
                          >
                            <FaPlus size="16px" />
                            Nueva plantilla
                          </Button>
                        </Stack>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {loading ? (
                  <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "15px" }}>
                    <Card.Body className="p-4 text-center">
                      <Spinner animation="border" variant="success" className="mb-2" />
                      <h5>Cargando plantillas...</h5>
                    </Card.Body>
                  </Card>
                ) : (
                  <>
                    <Row className="mb-4">
                      <Col md={4}>
                        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                          <Card.Body
                            className="text-center"
                            style={{
                              background: "linear-gradient(135deg, #1e7e34 0%, #20c997 100%)",
                              color: "white",
                              borderRadius: "15px",
                            }}
                          >
                            <h4 className="fw-bold">{templatesWithType}</h4>
                            <p className="mb-0">Con Tipo Definido</p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                          <Card.Body
                            className="text-center"
                            style={{
                              background: "linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)",
                              color: "white",
                              borderRadius: "15px",
                            }}
                          >
                            <h4 className="fw-bold">{templatesWithoutType}</h4>
                            <p className="mb-0">Sin Tipo Definido</p>
                          </Card.Body>
                        </Card>
                      </Col>
                      <Col md={4}>
                        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                          <Card.Body
                            className="text-center"
                            style={{
                              background: "linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)",
                              color: "white",
                              borderRadius: "15px",
                            }}
                          >
                            <h4 className="fw-bold">{filteredTemplates.length}</h4>
                            <p className="mb-0">Total Plantillas</p>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>

                    <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                      <Card.Header
                        className="text-white py-3"
                        style={{
                          background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                          borderRadius: "15px 15px 0 0",
                        }}
                      >
                        <h5 className="mb-0 fw-semibold">Listado de Plantillas</h5>
                      </Card.Header>
                      <Card.Body className="p-0">
                        {filteredTemplates.length > 0 ? (
                          <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                            <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                              <thead
                                style={{
                                  backgroundColor: "#f8f9fa",
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 10,
                                }}
                              >
                                <tr>
                                  <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "6%" }}>N°</th>
                                  <th className="border-0 py-3 px-3 text-secondary fw-semibold" style={{ width: "30%" }}>Plantilla</th>
                                  <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "14%" }}>Tipo</th>
                                  <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "22%" }}>Actualizado</th>
                                  <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "16%" }}>Creador</th>
                                  <th className="border-0 py-3 px-3 text-secondary fw-semibold text-center" style={{ width: "12%" }}>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredTemplates.map((item, index) => (
                                  <tr key={item.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                                    <td className="text-center py-3 px-3">
                                      <Badge bg="dark" className="px-2 py-1">
                                        {index + 1}
                                      </Badge>
                                    </td>
                                    <td className="py-3 px-3 fw-medium text-dark">{item.templateName || "Sin nombre"}</td>
                                    <td className="text-center py-3 px-3">
                                      <Badge bg="primary">{item.contractType || "-"}</Badge>
                                    </td>
                                    <td className="text-center py-3 px-3">
                                      <Badge bg="secondary">{formatTimestamp(item.updatedAt || item.createdAt)}</Badge>
                                    </td>
                                    <td className="text-center py-3 px-3">{item.createdBy || "-"}</td>
                                    <td className="text-center py-3 px-3">
                                      <Stack direction="horizontal" gap={1} className="justify-content-center">
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Editar en generador</Tooltip>}>
                                          <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => goToGenerator(item.id)}
                                            style={{ borderRadius: "8px" }}
                                          >
                                            <FaEdit size="14px" />
                                          </Button>
                                        </OverlayTrigger>
                                        <OverlayTrigger placement="top" overlay={<Tooltip>Eliminar plantilla</Tooltip>}>
                                          <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => handleDelete(item)}
                                            style={{ borderRadius: "8px" }}
                                          >
                                            <FaTrash size="14px" />
                                          </Button>
                                        </OverlayTrigger>
                                      </Stack>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <FaFileAlt size={32} className="text-muted mb-2" />
                            <h5 className="text-muted">No hay plantillas disponibles</h5>
                          </div>
                        )}
                      </Card.Body>
                    </Card>
                  </>
                )}
              </Tab>

              {/* PESTAÑA 4: HISTORIAL DE ENVÍOS */}
              <Tab eventKey="envios" title={<span>📜 Historial de Envíos</span>}>
                <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: "15px" }}>
                  <Card.Header
                    className="text-white py-3"
                    style={{
                      background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                      borderRadius: "15px 15px 0 0",
                    }}
                  >
                    <h5 className="mb-0 fw-semibold">Resultados de Envío en esta Sesión</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    {sendResults.length === 0 ? (
                      <div className="p-4 text-muted text-center">Aún no hay envíos realizados en esta sesión.</div>
                    ) : (
                      <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                        <Table hover responsive className="mb-0" style={{ fontSize: "0.9rem" }}>
                          <thead
                            style={{
                              backgroundColor: "#f8f9fa",
                              position: "sticky",
                              top: 0,
                              zIndex: 10,
                            }}
                          >
                            <tr>
                              <th className="text-secondary fw-semibold py-3 px-3">Fecha</th>
                              <th className="text-secondary fw-semibold py-3 px-3">Trabajador</th>
                              <th className="text-secondary fw-semibold py-3 px-3">Documento</th>
                              <th className="text-secondary fw-semibold py-3 px-3">Título Onboarding</th>
                              <th className="text-secondary fw-semibold py-3 px-3">Tipo Legajo</th>
                              <th className="text-secondary fw-semibold py-3 px-3 text-center">Estado</th>
                              <th className="text-secondary fw-semibold py-3 px-3">Detalle</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sendResults.map((result) => (
                              <tr key={result.id} style={{ borderBottom: "1px solid #f1f3f4" }}>
                                <td className="py-3 px-3">{result.fecha?.toLocaleString("es-PE") || "-"}</td>
                                <td className="py-3 px-3">{result.trabajador || "-"}</td>
                                <td className="py-3 px-3">{result.documento || "-"}</td>
                                <td className="py-3 px-3">{result.onboardingTitle || "-"}</td>
                                <td className="py-3 px-3">{result.legajoTipo || "-"}</td>
                                <td className="text-center py-3 px-3">
                                  <Badge bg={result.estado === "ENVIADO" ? "success" : "danger"}>
                                    {result.estado}
                                  </Badge>
                                </td>
                                <td className="py-3 px-3 text-muted">{result.detalle || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default ContratosPlantillas;
